import { useState } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useCatalog, type CatalogState } from '../hooks/useCatalog';
import { t } from '../i18n';
import CategorySidebar from '../components/CategorySidebar';
import TileGrid, { TileGridSkeleton } from '../components/TileGrid';
import EmptyState from '../components/EmptyState';

interface MarketplacePageProps {
  previewState?: CatalogState['tag'];
}

export default function MarketplacePage({ previewState }: MarketplacePageProps) {
  const config = useConfig();
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const { state, retry } = useCatalog({
    tiles: config.catalog.tiles,
    activeCategory,
    search,
    preview: previewState,
  });

  const allTiles = config.catalog.tiles;

  function clearFilters() {
    setActiveCategory('All');
    setSearch('');
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-8)', alignItems: 'flex-start' }}>
      <CategorySidebar
        tiles={allTiles}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
        {/* Search + eyebrow */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-4)' }}>
          <p className="dp-eyebrow">{t('marketplace.eyebrow')}</p>
          <input
            type="search"
            placeholder={t('marketplace.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: '6px var(--sp-3)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'var(--border-default)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
              width: '220px',
            }}
          />
        </div>

        {/* Data state machine */}
        {state.tag === 'loading' && <TileGridSkeleton count={allTiles.length || 6} />}

        {state.tag === 'loaded' && <TileGrid tiles={state.tiles} />}

        {state.tag === 'empty-no-results' && (
          <EmptyState variant="no-results" onAction={clearFilters} />
        )}

        {state.tag === 'empty-no-access' && (
          <EmptyState variant="no-access" />
        )}

        {state.tag === 'empty-fetch-failed' && (
          <EmptyState
            variant="fetch-failed"
            errorCode={state.errorCode}
            onAction={retry}
          />
        )}
      </div>
    </div>
  );
}
